import { useState } from "react";
import { AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader } from "@/components/ui/loader";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  OtcContractLayerDto,
  OtcContractLayersResponse,
  OtcLayerAmendmentType,
  useOtcContractLayers,
  useUpdateOtcContractCurrentLayer,
  useListAccounts,
} from "@/lib/api/otc";
import { useToast } from "@/components/ui/use-toast";
import { useGetTraderCustomersInfoBasic } from "@/lib/api/reports";
import {
  OtcLayerForm,
  OtcLayerFieldOptionData,
  OtcLayerSaveResult,
  OtcAmendmentWizard,
} from "@/section/otc/contract-form-v3";
import { generateAmendmentTitleForLayer } from "./common";
import { OtcContractLayerCard } from "./OtcContractLayerCard";

interface OtcContractDetailModalProps {
  open: boolean;
  contract_id: string | null;
  onClose: () => void;
}

function getDisplayLabel(
  display_snapshot: Record<string, unknown>,
  key: string,
): string | null {
  const value = display_snapshot[key];
  return typeof value === "string" ? value : null;
}

function OtcDetailHeader({
  contract_id,
  data,
}: {
  contract_id: string;
  data: OtcContractLayersResponse;
}) {
  const contract = data.contract;
  const contractNo =
    typeof contract.contract_no === "string" ? contract.contract_no : null;
  const isArchived = contract.is_archived === true;
  const isStaged = contract.staged_for_review === true;

  const currentLayer = data.layers.find(
    (layer: OtcContractLayerDto) => layer.id === data.current_layer_id,
  );
  const customerName = currentLayer
    ? getDisplayLabel(currentLayer.display_snapshot, "customer_name")
    : null;
  const traderName = currentLayer
    ? getDisplayLabel(currentLayer.display_snapshot, "trader_name")
    : null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 flex-wrap">
        <DialogTitle>
          OTC Contract {contractNo ? `#${contractNo}` : ''}
        </DialogTitle>
        {isArchived && <Badge variant="secondary">Archived</Badge>}
        {isStaged && <Badge variant="outline">Staged for review</Badge>}
      </div>
      <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
        {customerName && <span>Customer: {customerName}</span>}
        {traderName && <span>Trader: {traderName}</span>}
        <span>
          Current layer:{" "}
          {currentLayer ? `#${currentLayer.layer_no}` : "none"}
        </span>
      </div>
    </div>
  );
}

function OtcContractLayersBody({
  contract_id,
  data,
  onRefetch,
  onClose,
}: {
  contract_id: string;
  data: OtcContractLayersResponse;
  onRefetch: () => void;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const updateCurrentLayer = useUpdateOtcContractCurrentLayer();
  const traderCustomers = useGetTraderCustomersInfoBasic();
  const accountsQuery = useListAccounts(false);
  const [wizardOpen, setWizardOpen] = useState(false);

  const isArchived = data.contract.is_archived === true;
  const traders =
    traderCustomers.data && traderCustomers.data.success
      ? traderCustomers.data.data.traders
      : [];
  const customers =
    traderCustomers.data && traderCustomers.data.success
      ? traderCustomers.data.data.customers
      : [];
  const currentQuantity =
    typeof data.contract.quantity === "string"
      ? data.contract.quantity
      : typeof data.contract.quantity === "number"
        ? String(data.contract.quantity)
        : undefined;
  const currentLayerSnapshot = data.layers.find(
    (layer: OtcContractLayerDto) => layer.id === data.current_layer_id,
  )?.snapshot;
  const hasCurrentLayer = data.current_layer_id !== null;
  const canCreateAmendment =
    data.can_create_amendment && hasCurrentLayer && !isArchived;

  // Explain why amendments/edits are unavailable. Order matters: no current
  // layer first (nothing is editable), then archived, then max-layer cap.
  const amendmentBlockedReason: string | null = !hasCurrentLayer
    ? "This contract has no current layer, so it cannot be edited or amended. It may need to be migrated."
    : isArchived
      ? "This contract is archived. Re-open it to make changes or add amendments."
      : data.can_create_amendment_reason === "amendment_layer_pending_review"
        ? "This contract has an amendment pending review. Complete the review before creating another amendment."
      : !data.can_create_amendment
        ? "This contract has reached the current maximum supported amendment layers (slots 1\u20133)."
        : null;

  const handleAmended = (amendment_type: OtcLayerAmendmentType) => {
    if (amendment_type === "split") {
      toast({
        title: "Split created",
        description:
          "A new staged contract was created for the split. Find it in the staged/review list.",
      });
    } else {
      toast({ title: "Amendment created", description: "" });
    }
    onRefetch();
  };

  const handleStaleLayer = () => {
    toast({
      title: "Contract changed",
      description:
        "This contract was updated elsewhere. The latest layers have been reloaded.",
    });
    onRefetch();
  };

  const optionData: OtcLayerFieldOptionData = {
    customers:
      traderCustomers.data && traderCustomers.data.success
        ? traderCustomers.data.data.customers
        : [],
    traders:
      traderCustomers.data && traderCustomers.data.success
        ? traderCustomers.data.data.traders
        : [],
    accounts:
      accountsQuery.data && accountsQuery.data.success
        ? accountsQuery.data.data
        : [],
  };

  const onSaveCurrentLayer = async (
    layer_id: string,
    patch: Record<string, unknown>,
  ): Promise<OtcLayerSaveResult> => {
    const result = await updateCurrentLayer(contract_id, {
      layer_id,
      data: patch,
    });
    return {
      success: result.success,
      message: result.message,
      audit_id: result.success ? result.data.audit_id : null,
    };
  };

  return (
    <div className="flex flex-col gap-3">
      {canCreateAmendment && data.current_layer_id && (
        <div className="flex justify-end">
          <Button type="button" onClick={() => setWizardOpen(true)}>
            Add Amendment
          </Button>
        </div>
      )}

      {amendmentBlockedReason && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{amendmentBlockedReason}</AlertDescription>
        </Alert>
      )}

      {canCreateAmendment && data.current_layer_id && (
        <OtcAmendmentWizard
          open={wizardOpen}
          contract_id={contract_id}
          expected_current_layer_id={data.current_layer_id}
          traders={traders}
          customers={customers}
          currentQuantity={currentQuantity}
          currentSnapshot={currentLayerSnapshot}
          onClose={() => setWizardOpen(false)}
          onAmended={handleAmended}
          onStaleLayer={handleStaleLayer}
        />
      )}

      {data.layers.map((layer: OtcContractLayerDto) => {
        const isCurrent =
          layer.id === data.current_layer_id && layer.status === "current";
        if (isCurrent && !isArchived) {
          return (
            <div
              key={`${layer.id}-${layer.updated_at}`}
              className="rounded-lg border border-primary/10 p-4 flex flex-col gap-4"
            >
              <span className="font-medium">
                                              Edit {generateAmendmentTitleForLayer(layer) ?? ""}
              </span>
              <OtcLayerForm
                layer={layer}
                optionData={optionData}
                onSave={(patch: Record<string, unknown>) =>
                  onSaveCurrentLayer(layer.id, patch)
                }
                onSaveSuccess={() => {
                  toast({ title: "Contract updated", description: "" });
                  onClose();
                }}
                onStaleLayer={handleStaleLayer}
              />
            </div>
          );
        }
        return <OtcContractLayerCard key={layer.id} layer={layer} />;
      })}
    </div>
  );
}

export function OtcContractDetailModal({
  open,
  contract_id,
  onClose,
}: OtcContractDetailModalProps) {
  const query = useOtcContractLayers(open ? contract_id : null);
  const result = query.data;

  const onOpenChange = (next: boolean) => {
    if (!next) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-none w-[90vw] h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          {result && result.success && contract_id ? (
            <OtcDetailHeader contract_id={contract_id} data={result.data} />
          ) : (
            <DialogTitle>OTC Contract</DialogTitle>
          )}
        </DialogHeader>

        <div className="grow overflow-y-auto pr-1">
          {!contract_id && (
            <div className="text-sm text-muted-foreground">
              No contract selected.
            </div>
          )}

          {contract_id && query.isLoading && (
            <div className="py-12">
              <Loader />
            </div>
          )}

          {contract_id && !query.isLoading && result && !result.success && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Unable to load contract layers</AlertTitle>
              <AlertDescription>
                {result.message || "An unexpected error occurred."}
              </AlertDescription>
            </Alert>
          )}

          {contract_id &&
            !query.isLoading &&
            result &&
            result.success &&
            result.data.layers.length === 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>No layers found</AlertTitle>
                <AlertDescription>
                  This contract has no layers. It may not have been migrated
                  yet.
                </AlertDescription>
              </Alert>
            )}

          {contract_id &&
            !query.isLoading &&
            result &&
            result.success &&
            result.data.layers.length > 0 && (
              <OtcContractLayersBody
                contract_id={contract_id}
                data={result.data}
                onRefetch={() => {
                  void query.refetch();
                }}
                onClose={onClose}
              />
            )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
